"use client";

import React from "react";
import { Loader, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const ContactFormContent = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage
  } = useWvcForm();
  const {
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="24098" className="flex flex-col items-center justify-center text-center p-8 space-y-4 bg-primary/10 rounded-2xl border border-primary/20 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center text-primary mb-2">
          <Send className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-serif font-semibold text-primary">Richiesta Inviata</h3>
        <p className="text-muted-foreground font-sans max-w-md">
          {successMessage || "La Sua richiesta è stata presa in carico. Un nostro professionista La ricontatterà entro le prossime 24 ore lavorative."}
        </p>
      </div>;
  }
  return <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Il nome è obbligatorio."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-wider">Nome Completo</FormLabel>
              <FormControl>
                <Input placeholder="Mario Rossi" className="bg-background/50 border-border focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] font-sans">Nome e cognome del referente</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "L'indirizzo email è richiesto.",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Inserisca un indirizzo email valido."
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-wider">Indirizzo E-mail</FormLabel>
              <FormControl>
                <Input type="email" placeholder="m.rossi@esempio.it" className="bg-background/50 border-border focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="event_date" render={({
        field
      }) => {}} />

        <FormField control={control} name="guests" rules={{
        required: "Specificare il numero di referenti coinvolti.",
        min: {
          value: 1,
          message: "Minimo 1."
        },
        max: {
          value: 500,
          message: "Massimo 500."
        }
      }} render={({
        field
      }) => {}} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Descriva brevemente la Sua richiesta.",
      maxLength: {
        value: 1000,
        message: "Massimo 1000 caratteri."
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-wider">Dettagli della Consulenza</FormLabel>
            <FormControl>
              <Textarea placeholder="Descriva qui le finalità della Sua richiesta e i dettagli del caso." className="min-h-[120px] resize-y bg-background/50 border-border focus-visible:ring-primary font-sans" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm font-sans">
          {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[220px] h-12 text-sm font-mono uppercase tracking-widest bg-primary text-primary-foreground shadow-lg hover:opacity-90 transition-all duration-300">
          {isSubmitting ? <>
              <Loader className="mr-2 h-4 w-4 animate-spin" />
              Invio in corso...
            </> : "Invia Richiesta"}
        </Button>
      </div>
    </div>;
};
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="richiesta" className="py-32 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-20">
        <div className="absolute -top-[10%] -right-[5%] w-[600px] h-[600px] bg-primary/10 rounded-full blur-[120px]" />
        <div className="absolute top-[40%] -left-[10%] w-[500px] h-[500px] bg-secondary/5 rounded-full blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={cn("max-w-3xl mx-auto transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <div className="text-center mb-16 space-y-6">
            <h2 className="text-4xl text-primary tracking-tight !font-sans !font-normal md:text-6xl">Richiesta di Consulenza</h2>
            <p className="text-xl text-muted-foreground font-sans max-w-2xl mx-auto leading-relaxed">&nbsp;Contattaci per scoprire i nostri servizi</p>
          </div>

          <div className="bg-card/40 backdrop-blur-xl border border-border/60 rounded-[1rem] p-8 md:p-12 shadow-2xl transition-all duration-500">
            <div className="mb-10 pb-8 border-b border-border/30">
              <h3 className="text-2xl font-medium text-foreground !font-sans !not-italic">Informazioni di Contatto</h3>
              <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mt-2">
                I campi contrassegnati sono obbligatori
              </p>
            </div>

            <FormProvider formId="contact_form_contact" sectionName="contact" defaultValues={{
            name: "",
            email: "",
            phone: "",
            event_type: "",
            event_date: "",
            guests: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            event_type: {
              required: true
            },
            guests: {
              required: true,
              min: 1,
              max: 500
            },
            message: {
              required: true,
              maxLength: 1000
            }
          }} submitText="Invia Richiesta" successMessage="La Sua richiesta è stata inviata con successo. Riceverà un riscontro a breve." errorMessage="Si è verificato un errore durante l'invio. La preghiamo di riprovare.">
              <Form className="space-y-6">
                <ContactFormContent />
              </Form>
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}